import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getPositions,
  getPosition,
  createPosition,
  closePosition,
  updatePosition,
  reopenPosition,
  getPositionUsers,
  getLivePositions,
} from '@/lib/api'
import type { CreatePositionPayload, ReopenPositionPayload, UpdatePositionPayload } from '@/types'

export function usePositions(status?: 'open' | 'closed') {
  return useQuery({
    queryKey: ['positions', status],
    queryFn: () => getPositions(status),
  })
}

export function useOpenPositions() {
  return usePositions('open')
}

export function useLivePositions() {
  return useQuery({
    queryKey: ['positions', 'live'],
    queryFn: getLivePositions,
    refetchInterval: 30000,
  })
}

export function usePosition(positionId: string) {
  return useQuery({
    queryKey: ['positions', positionId],
    queryFn: () => getPosition(positionId),
    enabled: !!positionId,
  })
}

export function usePositionUsers() {
  return useQuery({
    queryKey: ['positions', 'users'],
    queryFn: getPositionUsers,
  })
}

export function useCreatePosition() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreatePositionPayload) => createPosition(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['positions'] })
    },
  })
}

export function useClosePosition() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ positionId, closedReason, closedResult }: { positionId: string; closedReason: string; closedResult: 'profit' | 'loss' }) =>
      closePosition(positionId, closedReason, closedResult),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['positions'] })
    },
  })
}

export function useUpdatePosition() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ positionId, payload }: { positionId: string; payload: UpdatePositionPayload }) =>
      updatePosition(positionId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['positions'] })
    },
  })
}

export function useReopenPosition() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ positionId, payload }: { positionId: string; payload?: ReopenPositionPayload }) =>
      reopenPosition(positionId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['positions'] })
    },
  })
}
